import type { Tab, TabRequest, WidgetOptions } from './context';
import LAUNCHER_CSS from './css/launcher.css?raw';
import type { DemoOptions } from './demo-transport';
import { h, PLAY_SVG, svg } from './h';
import type { InlineApi, LauncherHooks, PanelApi } from './panel';
import { forgetOtherScopes } from './chat-key';
import { rememberSource } from './source';
import { bookingRedirectUrl } from './booking-redirect';
import type { Transport } from './transport';

/**
 * The only code that loads with the page: reads the script tag, draws the launcher button
 * and waits. The panel, the views and the transport are fetched when someone hovers or
 * clicks the launcher, so the widget costs the website one small script.
 *
 * Embed:
 *   <script type="module" src="https://assist.kredin.no/widget.js" data-kredin-assist
 *           data-api="https://assist.kredin.no" data-channel="website"></script>
 *
 * Any element with data-kredin-open="prisy|booking|kreddy" opens that tab, so existing
 * "Book en gjennomgang" links can keep their href as a fallback.
 *
 * In the page itself, for example a calculator on a landing page:
 *   <div data-kredin-embed="prisy"></div>
 * The value lists the tabs, the first one shown: "prisy" is the calculator alone, and
 * "kreddy,prisy,booking" all three; empty means the script's tabs. A tab the script does not
 * have (data-tabs) is left out. data-height sets the height
 * in pixels (default 620), and data-launcher="off" hides the corner button on that page.
 *
 * Single-page apps like Kredin draw the element after the script has run. They call
 * `KredinAssist.mount()` when the element is on the page; mount() reads the script tag's data
 * attributes again, so the app can change data-user-name, data-company and data-session-scope
 * first. data-session-scope (the user and the company) gives each its own conversation, so a
 * company switch anywhere in the app can never reopen the previous company's chat.
 * `KredinAssist.forget()` drops the saved chats, for example at logout. The
 * script tag is still needed once per page.
 */

const ALL_TABS: Tab[] = ['kreddy', 'prisy', 'booking'];
const TEASER_KEY = 'kredin-assist:teaser-closed';
const INLINE_HEIGHT = 620;
const isTab = (t: unknown): t is Tab => ALL_TABS.includes(t as Tab);
const readTabs = (list: string | undefined) => (list ?? '').split(',').map((t) => t.trim()).filter(isTab);

type Modules = () => Promise<[typeof import('./panel'), Transport]>;

function readOptions(script: HTMLScriptElement | null): WidgetOptions {
  const d = script?.dataset ?? {};
  const channel = d.channel === 'portal' ? 'portal' : 'website';
  const requested = readTabs(d.tabs ?? ALL_TABS.join(','));
  // Prices belong on the website only; in Kredin the meeting is with support.
  const tabs = channel === 'portal' ? requested.filter((t) => t !== 'prisy') : requested;
  const demo: DemoOptions | undefined =
    d.demo === undefined
      ? undefined
      : {
          hours: d.demoHours === 'open' || d.demoHours === 'closed' ? d.demoHours : 'real',
          nobodyAnswers: d.demoNobody !== undefined,
          speed: d.demoSpeed === undefined ? undefined : Number(d.demoSpeed),
          claimAfterMs: d.demoClaim === undefined ? undefined : Number(d.demoClaim),
          failures: d.demoFail,
        };
  return {
    channel,
    tabs: tabs.length > 0 ? tabs : ['kreddy'],
    api: d.api ?? '',
    label: d.label ?? (channel === 'portal' ? 'Hjelp' : 'Spør oss'),
    teaser: d.teaser,
    teaserSub: d.teaserSub,
    teaserTab: isTab(d.teaserTab) ? d.teaserTab : 'prisy',
    cookieSelector: d.cookieSelector,
    userEmail: d.userEmail,
    userName: d.userName,
    company: d.company,
    sessionScope: d.sessionScope,
    identityToken: d.identityToken,
    seller: channel === 'website' ? ownSellerFrom(d.seller ?? new URLSearchParams(location.search).get('selger')) : undefined,
    bookingRedirect: channel === 'website' ? d.bookingRedirect : undefined,
    demo,
  };
}

/** A seller id from the script tag or the page's address, as the Worker accepts it. */
const ownSellerFrom = (v: string | null | undefined) => (v && /^[a-z0-9-]{1,40}$/.test(v) ? v : undefined);

function session(key: string, value?: string): string | null {
  try {
    if (value !== undefined) sessionStorage.setItem(key, value);
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Cookiebot's icon sits in the bottom left corner of the page, over the chat's input when the
 * panel fills a phone screen. It is hidden while the panel is open and back when it closes.
 */
function hideCookieIconWhile(open: boolean) {
  if (!document.getElementById('kredin-assist-open-style')) {
    const style = document.createElement('style');
    style.id = 'kredin-assist-open-style';
    style.textContent = 'html.kredin-assist-open #CookiebotWidget { display: none !important; }';
    document.head.append(style);
  }
  document.documentElement.classList.toggle('kredin-assist-open', open);
}

/** The button in the corner, and the panel it opens. */
function startLauncher(options: WidgetOptions, theme: string | undefined, modules: Modules): PanelApi {
  const host = h('div', { id: 'kredin-assist' });
  if (theme) host.setAttribute('data-theme', theme);
  host.setAttribute('data-channel', options.channel);
  const root = host.attachShadow({ mode: 'open' });
  root.append(h('style', {}, LAUNCHER_CSS));

  let unread = 0;
  const badge = h('span', { class: 'k-badge', hidden: true, 'aria-hidden': 'true' });
  const launcher = h(
    'button',
    { class: 'k-launcher', type: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', 'aria-label': options.label },
    svg(PLAY_SVG),
    h('span', { class: 'k-launcher-label' }, options.label),
    badge,
  );
  root.append(launcher);

  let panel: Promise<PanelApi> | undefined;
  let teaser: HTMLElement | null = null;

  const hooks: LauncherHooks = {
    unread(preview: HTMLElement) {
      unread += 1;
      badge.textContent = String(unread);
      badge.hidden = false;
      launcher.setAttribute('aria-label', `${options.label}, ${unread} ${unread === 1 ? 'ny melding' : 'nye meldinger'}`);
      showTeaser(preview, true);
    },
    toggled(isOpen: boolean) {
      hideCookieIconWhile(isOpen);
      if (!isOpen) return;
      unread = 0;
      badge.hidden = true;
      launcher.setAttribute('aria-label', options.label);
      teaser?.remove();
      teaser = null;
    },
  };

  const load = () => (panel ??= modules().then(([m, transport]) => m.mountPanel(root, launcher, options, transport, hooks)));
  const open = async (tab?: Tab, request?: TabRequest) => (await load()).open(tab, request);
  const prefetch = () => void load().then((p) => p.prefetch());

  function showTeaser(content: HTMLElement, message = false) {
    teaser?.remove();
    teaser = h(
      'div',
      { class: `k-teaser${message ? ' k-teaser--msg' : ''}` },
      content,
      h('button', {
        class: 'k-teaser-close',
        type: 'button',
        'aria-label': 'Skjul',
        onclick: () => {
          if (!message) session(TEASER_KEY, '1');
          teaser?.remove();
          teaser = null;
        },
      }, svg('<svg viewBox="0 0 14 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12"/></svg>')),
    );
    teaser.querySelector<HTMLElement>('.k-teaser-close .k-icon')!.style.cssText = 'width:12px;height:12px';
    root.append(teaser);
  }

  launcher.addEventListener('click', () => void open());
  launcher.addEventListener('pointerenter', prefetch, { once: true });
  launcher.addEventListener('focus', prefetch, { once: true });

  // The offer on the pricing page; once closed it stays away for the rest of the visit.
  if (options.teaser && session(TEASER_KEY) === null) {
    showTeaser(
      h(
        'button',
        { class: 'k-teaser-open', type: 'button', onclick: () => void open(options.teaserTab) },
        h('span', { class: 'k-teaser-mark', 'aria-hidden': 'true' }, '='),
        h('span', {}, h('b', {}, options.teaser), options.teaserSub ? h('small', {}, options.teaserSub) : ''),
      ),
    );
  }

  // Move above the site's cookie banner while it is visible, and shrink to a round button on phones.
  if (options.cookieSelector) {
    const banner = (() => {
      try {
        return document.querySelector<HTMLElement>(options.cookieSelector!);
      } catch {
        return null;
      }
    })();
    const sync = () => {
      const height = banner && banner.isConnected && banner.offsetParent !== null ? banner.getBoundingClientRect().height : 0;
      host.style.setProperty('--k-offset-bottom', `${Math.round(height)}px`);
      host.toggleAttribute('data-cookie', height > 0);
      launcher.classList.toggle('k-launcher--icon', height > 0 && window.innerWidth <= 480);
    };
    if (banner && 'ResizeObserver' in window) new ResizeObserver(sync).observe(banner);
    // Banners are usually closed with a click; check again just after any click.
    document.addEventListener('click', () => setTimeout(sync, 400), true);
    window.addEventListener('resize', sync);
    sync();
  }

  document.body.append(host);
  return { open: (tab, request) => void open(tab, request), close: () => void load().then((p) => p.close()), prefetch };
}

interface Embed {
  el: HTMLElement;
  first: Tab;
  mount(): Promise<InlineApi>;
}

/** Every <div data-kredin-embed> on the page, each loaded when it comes near the screen. */
function mountEmbeds(options: WidgetOptions, theme: string | undefined, modules: Modules): Embed[] {
  const out: Embed[] = [];
  for (const el of document.querySelectorAll<HTMLElement>('[data-kredin-embed]')) {
    if (el.shadowRoot) continue;
    const d = el.dataset;
    // Only tabs the site has turned on; the portal has the chat alone.
    const listed = readTabs(d.kredinEmbed).filter((t) => options.tabs.includes(t));
    const tabs = listed.length > 0 ? listed : options.tabs;
    const height = Number(d.height) > 0 ? Number(d.height) : INLINE_HEIGHT;
    // The space is held from the start, so nothing on the page moves when the widget arrives.
    el.style.setProperty('--k-inline-height', `${height}px`);
    el.style.display = 'block';
    el.style.minHeight = `${height}px`;
    if (theme) el.setAttribute('data-theme', theme);
    el.setAttribute('data-channel', options.channel);
    const root = el.attachShadow({ mode: 'open' });
    root.append(h('style', {}, LAUNCHER_CSS));

    let api: Promise<InlineApi> | undefined;
    // data-layout="page": the booking page's own two-column card instead of the panel.
    const page = d.layout === 'page';
    const mount = () =>
      (api ??= page
        ? modules().then(([, transport]) => import('./booking-page').then((p) => {
            const redirect = bookingRedirectUrl(d.kredinRedirect ?? options.bookingRedirect, location.href)
              ?? `${(options.api || new URL(import.meta.url).origin).replace(/\/$/, '')}/booking/takk`;
            return p.mountBookingPage(root, options, transport, options.channel === 'website' ? () => location.assign(redirect) : undefined);
          }))
        : modules().then(([m, transport]) => m.mountInline(root, { ...options, tabs }, transport, options.tabs)));
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          seen.disconnect();
          void mount();
        },
        { rootMargin: '400px' },
      );
      seen.observe(el);
    } else void mount();
    out.push({ el, first: tabs[0]!, mount });
  }
  return out;
}

function start() {
  if (document.getElementById('kredin-assist') || (window as unknown as { KredinAssist?: unknown }).KredinAssist) return;
  rememberSource();
  const script = document.querySelector<HTMLScriptElement>('script[data-kredin-assist]');
  const options = readOptions(script);
  if (options.sessionScope) forgetOtherScopes(options.channel, options.sessionScope);
  const theme = script?.dataset.theme;

  let loaded: ReturnType<Modules> | undefined;
  const modules: Modules = () =>
    (loaded ??= Promise.all([
      import('./panel'),
      options.demo
        ? import('./demo-transport').then((m) => m.demoTransport({ ...options.demo, support: options.channel === 'portal' }))
        : import('./transport').then((m) => m.httpTransport({ api: options.api, channel: options.channel, identityToken: options.identityToken, seller: options.seller })),
    ]));

  const embeds = mountEmbeds(options, theme, modules);
  // Elements a single-page app draws later; the script tag is read again for who is logged in.
  const mount = () => embeds.push(...mountEmbeds(readOptions(script), theme, modules));
  const forget = () => {
    try {
      for (const key of Object.keys(sessionStorage)) if (key.startsWith(`kredin-assist:chat:${options.channel}`)) sessionStorage.removeItem(key);
    } catch {
      // Storage blocked: there was no saved chat to forget.
    }
  };
  // A landing page can have the widget in its content only, without the button in the corner.
  const withLauncher = script?.dataset.launcher !== 'off' && !embeds.some((e) => e.el.dataset.launcher === 'off');
  const launcher = withLauncher ? startLauncher(options, theme, modules) : undefined;

  let bookingPopup: Promise<import('./booking-popup').BookingPopup> | undefined;
  const openBooking = async (settings: { redirectUrl?: string } = {}) => {
    // The portal keeps its existing support booking and signed identity flow.
    if (options.channel !== 'website' || !options.tabs.includes('booking')) return open('booking');
    launcher?.close();
    bookingPopup ??= modules().then(([, transport]) => import('./booking-popup').then(m => m.mountBookingPopup(options, transport)));
    (await bookingPopup).open(settings.redirectUrl ?? options.bookingRedirect);
  };

  const open = async (tab?: Tab, request?: TabRequest) => {
    if (launcher) return launcher.open(tab, request);
    // Without the corner button, links go to the first widget in the page.
    const embed = embeds.find((e) => e.el.isConnected);
    if (!embed) return;
    embed.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (await embed.mount()).show(tab ?? embed.first, request);
  };
  const prefetch = () => (launcher ? launcher.prefetch() : void embeds.find((e) => e.el.isConnected)?.mount());

  document.addEventListener('click', (e) => {
    const el = (e.target as Element | null)?.closest?.<HTMLElement>('[data-kredin-open], [data-kredin-popup="booking"]');
    if (!el) return;
    e.preventDefault();
    if (el.dataset.kredinPopup === 'booking') {
      void openBooking({ redirectUrl: el.dataset.kredinRedirect });
      return;
    }
    const tab = el.dataset.kredinOpen;
    void open(isTab(tab) ? tab : undefined);
  });
  document.addEventListener('pointerover', (e) => {
    if ((e.target as Element | null)?.closest?.('[data-kredin-open]')) prefetch();
  });

  (window as unknown as { KredinAssist: unknown }).KredinAssist = {
    open,
    openBooking,
    close: () => { launcher?.close(); void bookingPopup?.then(p => p.close()); },
    prefetch,
    mount,
    forget,
  };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
